import { useEffect, useState } from 'react';

/** Une ligne de « Quoi de neuf » : le français est toujours là, l anglais est facultatif. */
export interface GsWhatsNewLine {
  fr: string;
  en?: string;
}

/** Ce qui a changé à un passage en production (lot feat-quoi-de-neuf-par-app). */
export interface GsWhatsNewPassage {
  etiquette: string;
  /** `AAAA-MM-JJ`. */
  date: string;
  lignes: GsWhatsNewLine[];
}

/**
 * Relatif, comme `/api/version` : le fichier est servi par l origine de la page (il vient de
 * `apps/<app>/public/nouveautes.json`, copié tel quel dans la construction).
 */
const NOUVEAUTES_ENDPOINT = '/nouveautes.json';

function texte(valeur: unknown): string | null {
  return typeof valeur === 'string' && valeur.trim() !== '' ? valeur.trim() : null;
}

/**
 * Lit le corps de `nouveautes.json`. Ne garde que les passages RELUS (`relu: true`) : un brouillon
 * généré ne s affiche jamais. Tout ce qui n est pas reconnu disparaît, sans lever.
 */
export function parseWhatsNew(data: unknown): GsWhatsNewPassage[] {
  const passages = (data as { passages?: unknown } | null)?.passages;
  if (!Array.isArray(passages)) return [];
  const gardes: GsWhatsNewPassage[] = [];
  for (const brut of passages) {
    const p = brut as { etiquette?: unknown; date?: unknown; relu?: unknown; lignes?: unknown } | null;
    if (!p || p.relu !== true) continue;
    const etiquette = texte(p.etiquette);
    const date = texte(p.date);
    if (!etiquette || !date || !Array.isArray(p.lignes)) continue;
    const lignes: GsWhatsNewLine[] = [];
    for (const l of p.lignes) {
      const fr = texte((l as { fr?: unknown } | null)?.fr);
      if (!fr) continue;
      const en = texte((l as { en?: unknown }).en);
      lignes.push(en ? { fr, en } : { fr });
    }
    if (lignes.length) gardes.push({ etiquette, date, lignes });
  }
  return gardes;
}

async function lire(fetchFn: typeof fetch): Promise<GsWhatsNewPassage[]> {
  try {
    const res = await fetchFn(NOUVEAUTES_ENDPOINT, { cache: 'no-cache', credentials: 'same-origin' });
    // Un serveur de SPA rend souvent `index.html` (200) pour un chemin inconnu : le corps n est
    // alors pas du JSON, `json()` leve, et la réponse devient « rien de neuf ».
    return res.ok ? parseWhatsNew(await res.json()) : [];
  } catch (err: unknown) {
    // Une page de nouveautés n est pas une fonction de l app : l absence se RETIENT comme une
    // liste vide (état, pas silence) et n empêche rien d autre de s afficher.
    void err;
    return [];
  }
}

// Une lecture par page, PARTAGÉE entre le lien et le tiroir. Même tenue du cache que
// `lireBuildInfo` : par la fonction `fetch`, donc un test qui la remplace repart d une lecture neuve.
const lectures = new WeakMap<typeof fetch, Promise<GsWhatsNewPassage[]>>();

export function lireNouveautes(): Promise<GsWhatsNewPassage[]> {
  if (typeof fetch === 'undefined') return Promise.resolve([]);
  const fetchFn = fetch;
  let lecture = lectures.get(fetchFn);
  if (!lecture) {
    lecture = lire(fetchFn);
    lectures.set(fetchFn, lecture);
  }
  return lecture;
}

/**
 * Les passages relus de l app, le plus récent d abord, ou `null` tant que la lecture n est pas
 * revenue. `[]` : pas de fichier, fichier illisible, ou rien de relu — donc aucun lien à montrer.
 */
export function useWhatsNew(): GsWhatsNewPassage[] | null {
  const [passages, setPassages] = useState<GsWhatsNewPassage[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void lireNouveautes().then((next) => {
      if (!cancelled) setPassages(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return passages;
}
